import { useEffect, useState, type FormEvent } from "react";
import { Icon } from "@/components/ui/Icon";
import { HomeAppSection } from "@/components/a2hs/HomeAppSection";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { useConfirm } from "@/hooks/useConfirm";
import { useMyDevices } from "@/hooks/useMyDevices";
import { renameMyDevice, setMyDeviceAutoLogin } from "@/services/devices.repository";
import { watchMyAccessLog, writeSelfAccessLog } from "@/services/access_log.repository";
import { runAccountOp } from "@/services/account_ops.service";
import { selfLog } from "@/services/device_session.service";
import { ACCESS_LOG_TYPE_LABEL, MAX_DEVICES, type AccessLogEntry, type DeviceDoc } from "@/domain/session";
import { DEVICE_NAME_MAX, accessLogTone, formatDateTime, normalizeDeviceName } from "@/domain/device_info";

/**
 * DevicesTab — D-3 기기관리(12 설계 §8-D D-3 · §7-5 · §13 ②).
 *  - 내 활성 기기 카드: 현재 기기 = 파란 틴트·"현재 기기" 배지·로그아웃 버튼 없음
 *  - 다른 기기: [자동로그인 해제/켜기](기기 문서 직접) · [로그아웃](self.device_revoke logout) · [삭제](release)
 *    기기 단위 로그아웃은 소프트 — 그 기기가 켜져 있으면 바로, 아니면 다음 접속 때(§13 ②)
 *  - [다른 기기 모두 로그아웃] = self.logout_all (warning)
 *  - 내 접속 기록 최근 5건
 */
const LOG_LIMIT = 5;

export function DevicesTab() {
  const { user, sharedSession } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const { devices, loading, error, currentId } = useMyDevices();
  const [logs, setLogs] = useState<AccessLogEntry[] | null>(null);
  const [logErr, setLogErr] = useState<Error | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const uid = user?.uid ?? null;

  useEffect(() => {
    if (!uid) return;
    return watchMyAccessLog(
      uid,
      LOG_LIMIT,
      (rows) => {
        setLogs(rows);
        setLogErr(null);
      },
      (e) => {
        setLogErr(e);
        setLogs([]);
      },
    );
  }, [uid]);

  if (!user) return null;
  const others = devices.filter((d) => d.id !== currentId);
  const count = devices.length;
  const free = user.isAdmin ? 0 : Math.max(0, MAX_DEVICES - count);

  const devLog = (d: DeviceDoc) => ({ id: d.id, name: d.name, browser: d.browser, os: d.os, kind: d.kind });

  async function saveName(e: FormEvent, d: DeviceDoc) {
    e.preventDefault();
    if (!user) return;
    const name = normalizeDeviceName(draft);
    if (!name) {
      toast.error("기기 이름을 입력하세요.");
      return;
    }
    if (name === d.name) {
      setEditing(null);
      return;
    }
    setBusyId(d.id);
    try {
      await renameMyDevice(user.uid, d.id, name);
      await writeSelfAccessLog(selfLog(user, "device_rename", { ...devLog(d), name }));
      setEditing(null);
      toast.success("기기 이름을 바꿨습니다.");
    } catch (err) {
      toast.error(`이름을 바꾸지 못했습니다: ${(err as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function toggleAutoLogin(d: DeviceDoc) {
    if (!user) return;
    const next = !d.auto_login;
    if (!next) {
      const ok = await confirm({
        title: "자동 로그인을 끌까요?",
        summary: `'${d.name}'`,
        effects: [
          { text: "그 기기는 브라우저(앱)를 닫으면 로그아웃됩니다.", strong: true },
          "기기 등록은 그대로 남습니다 · 다시 켤 수 있습니다",
        ],
        confirmLabel: "자동 로그인 끄기",
        kind: "warning",
      });
      if (!ok) return;
    }
    setBusyId(d.id);
    try {
      await setMyDeviceAutoLogin(user.uid, d.id, next);
      if (!next) await writeSelfAccessLog(selfLog(user, "auto_login_off", devLog(d)));
      toast.success(next ? "자동 로그인을 켰습니다. 다음 로그인부터 유지됩니다." : "자동 로그인을 껐습니다.");
    } catch (err) {
      toast.error(`바꾸지 못했습니다: ${(err as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function revoke(d: DeviceDoc, mode: "logout" | "release") {
    const ok =
      mode === "logout"
        ? await confirm({
            title: "이 기기를 로그아웃할까요?",
            summary: `'${d.name}' · ${d.browser} · ${d.os}`,
            effects: [
              { text: "그 기기가 켜져 있으면 바로, 꺼져 있으면 다음 접속 때 로그아웃됩니다.", strong: true },
              "분실처럼 확실히 끊어야 하면 [다른 기기 모두 로그아웃]을 쓰세요",
            ],
            confirmLabel: "로그아웃",
            kind: "info",
          })
        : await confirm({
            title: "이 기기를 삭제할까요?",
            summary: `'${d.name}' · ${d.browser} · ${d.os}`,
            effects: [
              { text: "기기 등록을 해제해 자리 하나를 비웁니다.", strong: true },
              "그 기기는 다음 접속 때 로그아웃되고, 다시 쓰려면 로그인해 새로 등록합니다",
            ],
            confirmLabel: "삭제",
            kind: "danger",
          });
    if (!ok) return;
    setBusyId(d.id);
    try {
      await runAccountOp("self.device_revoke", { device_id: d.id, mode });
      toast.success(mode === "logout" ? `'${d.name}' 로그아웃을 요청했습니다.` : `'${d.name}' 등록을 해제했습니다.`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function logoutOthers() {
    const ok = await confirm({
      title: "다른 기기를 모두 로그아웃할까요?",
      summary: `이 기기를 뺀 ${others.length}대`,
      effects: [
        { text: "켜져 있는 기기는 바로, 꺼져 있는 기기는 다음 접속 때 로그아웃됩니다.", strong: true },
        "이 기기는 로그인 상태가 유지됩니다",
        "다른 기기에서는 다시 로그인해야 합니다",
      ],
      confirmLabel: "모두 로그아웃",
      kind: "warning",
    });
    if (!ok) return;
    setBusyId("__all");
    try {
      await runAccountOp("self.logout_all", { keep_device_id: currentId });
      toast.success("다른 기기를 모두 로그아웃했습니다.");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="ma-pane">
      {/* 모바일만: 홈 화면 앱 설치 상태 · [추가] = 설치 안내 다시 열기 */}
      <HomeAppSection />
      <section className="ma-sec">
        <div className="ma-sec-hd ma-dev-hd">
          <h3 className="ma-sec-title">
            {loading || error ? "등록 기기" : user.isAdmin ? `등록 기기 ${count}` : `등록 기기 ${count}/${MAX_DEVICES}`}
          </h3>
          <span className="ma-help">
            {user.isAdmin ? "전체 관리자는 기기 수 제한이 없습니다" : "PC·휴대폰 합쳐 3대까지 · 전체 관리자는 제한 없음"}
          </span>
          {others.length > 0 && (
            <button
              type="button"
              className="btn-ghost is-danger ma-dev-all"
              onClick={() => void logoutOthers()}
              disabled={busyId !== null}
            >
              다른 기기 모두 로그아웃
            </button>
          )}
        </div>

        {sharedSession && (
          <div className="ma-note" role="note">
            <Icon name="info" size={16} />
            <span>지금은 공용 PC로 로그인해 이 기기는 등록되지 않았습니다. 창을 닫으면 로그아웃됩니다.</span>
          </div>
        )}

        {loading ? (
          <div className="ma-skel-list" aria-busy="true">
            <span className="ma-skel is-tall" />
            <span className="ma-skel is-tall" />
          </div>
        ) : error ? (
          <div className="ma-note is-error" role="alert">
            <Icon name="warning" size={16} />
            <span>기기 목록을 불러오지 못했습니다. 서버 준비가 끝나면 다시 열어 보세요. ({error.message})</span>
          </div>
        ) : (
          <div className="ma-dev-list">
            {devices.map((d) => {
              const isCur = d.id === currentId;
              const isBusy = busyId === d.id;
              return (
                <div className={`ma-card ma-dev${isCur ? " is-current" : ""}`} key={d.id}>
                  <div className="ma-dev-top">
                    {editing === d.id ? (
                      <form className="ma-dev-rename" onSubmit={(e) => void saveName(e, d)}>
                        <input
                          value={draft}
                          maxLength={DEVICE_NAME_MAX}
                          onChange={(e) => setDraft(e.target.value)}
                          aria-label="기기 이름"
                          autoFocus
                          disabled={isBusy}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              e.stopPropagation();
                              setEditing(null);
                            }
                          }}
                        />
                        <button type="submit" className="btn-primary btn-tiny" disabled={isBusy}>
                          저장
                        </button>
                        <button type="button" className="btn-ghost btn-tiny" onClick={() => setEditing(null)} disabled={isBusy}>
                          취소
                        </button>
                      </form>
                    ) : (
                      <>
                        <span className="ma-dev-name">{d.name}</span>
                        {isCur && <span className="badge ma-badge-current">현재 기기</span>}
                        <button
                          type="button"
                          className="ma-icon-btn"
                          onClick={() => {
                            setEditing(d.id);
                            setDraft(d.name);
                          }}
                          aria-label={`${d.name} 이름 변경`}
                          title="이름 변경"
                        >
                          <Icon name="pencil" size={14} />
                        </button>
                      </>
                    )}
                  </div>
                  <div className="ma-help">
                    {d.browser} · {d.os} · 처음 등록 {formatDateTime(d.created_at, true)} · 마지막 접속{" "}
                    {isCur ? "지금" : formatDateTime(d.last_seen_at)}
                  </div>
                  <div className="ma-dev-bottom">
                    <span className="ma-dev-auto">
                      자동 로그인 <span className={`badge${d.auto_login ? " badge-ok" : ""}`}>{d.auto_login ? "켜짐" : "꺼짐"}</span>
                      {d.revoke_requested_at && !isCur && <span className="badge badge-warn">로그아웃 대기 · 다음 접속 때</span>}
                    </span>
                    <span className="ma-dev-actions">
                      <button type="button" className="btn-ghost btn-tiny" onClick={() => void toggleAutoLogin(d)} disabled={isBusy}>
                        {d.auto_login ? "자동로그인 해제" : "자동로그인 켜기"}
                      </button>
                      {!isCur && (
                        <>
                          <button type="button" className="btn-ghost btn-tiny" onClick={() => void revoke(d, "logout")} disabled={isBusy}>
                            로그아웃
                          </button>
                          <button type="button" className="btn-ghost btn-tiny is-danger" onClick={() => void revoke(d, "release")} disabled={isBusy}>
                            삭제
                          </button>
                        </>
                      )}
                    </span>
                  </div>
                  {isCur && <div className="ma-help">이 기기에서 로그아웃하려면 계정 메뉴의 [로그아웃]을 쓰세요</div>}
                </div>
              );
            })}
            {free > 0 && (
              <div className="ma-dev-free">빈 자리 {free} — 새 기기에서 로그인하면 자동으로 등록됩니다</div>
            )}
            <p className="ma-help">홈 화면 앱과 Safari, Chrome 과 Edge 는 서로 다른 기기로 셉니다.</p>
          </div>
        )}
      </section>

      <section className="ma-sec">
        <div className="ma-sec-hd">
          <h3 className="ma-sec-title">내 접속 기록</h3>
          <span className="ma-help">최근 {LOG_LIMIT}건 · 5년 보관</span>
        </div>
        {logs === null ? (
          <div className="ma-skel-list" aria-busy="true">
            <span className="ma-skel" />
            <span className="ma-skel" />
          </div>
        ) : logs.length === 0 ? (
          <div className="empty-state ma-empty">
            <Icon name="calendar" size={24} />
            <div className="empty-msg">아직 접속 기록이 없습니다</div>
            <div className="ma-help">
              {logErr ? "기록을 불러오지 못했습니다. 서버 준비가 끝나면 보입니다." : "이 기능을 켠 뒤부터 기록됩니다 · 5년 보관"}
            </div>
          </div>
        ) : (
          <table className="tbl ma-log-tbl">
            <thead>
              <tr>
                <th>시각</th>
                <th>기기</th>
                <th>종류</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{formatDateTime(r.created_at)}</td>
                  <td>{r.shared_pc ? "공용 PC" : r.device_name || "—"}</td>
                  <td>
                    <span className={`ma-tone is-${accessLogTone(r.type)}`}>{ACCESS_LOG_TYPE_LABEL[r.type]}</span>
                    {r.by_name && r.by_uid !== uid && <span className="ma-help"> · 처리 {r.by_name}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
